import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'

function RingChart ({
  seriesId,
  name,
  colorBy,
  zLevel,
  z,
  center,
  radius,
  legendHoverLink,
  startAngle,
  endAngle,
  clockwise,
  data,
  min,
  max,
  splitNumber,
  axisLine,
  progress,
  splitLine,
  axisTick,
  axisLabel,
  pointer,
  anchor,
  itemStyle,
  emphasis,
  title,
  detail,
  markPoint,
  markLine,
  markArea,
  silent,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationEasingUpdate,
  animationDelayUpdate,
  tooltip
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    setSeries(seriesId, {
      type: 'gauge',
      id: seriesId,
      name,
      colorBy,
      zLevel,
      z,
      center,
      radius,
      legendHoverLink,
      startAngle,
      endAngle,
      clockwise,
      data,
      min,
      max,
      splitNumber,
      axisLine,
      progress,
      splitLine,
      axisTick,
      axisLabel,
      pointer,
      anchor,
      itemStyle,
      emphasis,
      title,
      detail,
      markPoint,
      markLine,
      markArea,
      silent,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationEasingUpdate,
      animationDelayUpdate,
      tooltip
    })
  }, [
    setSeries,
    seriesId,
    name,
    colorBy,
    zLevel,
    z,
    center,
    radius,
    legendHoverLink,
    startAngle,
    endAngle,
    clockwise,
    data,
    min,
    max,
    splitNumber,
    axisLine,
    progress,
    splitLine,
    axisTick,
    axisLabel,
    pointer,
    anchor,
    itemStyle,
    emphasis,
    title,
    detail,
    markPoint,
    markLine,
    markArea,
    silent,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationEasingUpdate,
    animationDelayUpdate,
    tooltip
  ])

  return null
}

RingChart.propTypes = {
  seriesId: PropTypes.string,
  name: PropTypes.string,

  /** The policy to take color from option.color. Valid values:

   'series': assigns the colors in the palette by series, so that all data in the same series are in the same color;
   'data': assigns colors in the palette according to data items, with each data item using a different color. **/
  colorBy: PropTypes.oneOf(['series', 'data']),
  /** zlevel value of all graphical elements in .

   zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in different Canvases, which is a common optimization technique. We can put those frequently changed elements (like those with animations) to a separate zlevel. Notice that too many Canvases will increase memory cost, and should be used carefully on mobile phones to avoid crash.

   Canvases with bigger zlevel will be placed on Canvases with smaller zlevel. **/
  zLevel: PropTypes.number,
  /** z value of all graphical elements in , which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.

   z has a lower priority to zlevel, and will not create new Canvas. **/
  z: PropTypes.number,
  /** Center position of , the first of which is the horizontal position, and the second is the vertical position.

   Percentage is supported. When set in percentage, the item is relative to the container width, and the second item to the height. **/
  center: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /** The radius of gauge chart. It can be a percentage value of the smaller of container half width and half height, also can be an absolute value. **/
  radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Whether to enable highlighting chart when legend is being hovered. **/
  legendHoverLink: PropTypes.bool,
  /** The start angle of gauge chart. The direct right side of circle center is 0 degree, the right above it is 90 degree, the direct left side of it is 180 degree. **/
  startAngle: PropTypes.number,
  /** The end angle of gauge chart. **/
  endAngle: PropTypes.number,
  /** Whether the scale in gauge chart increases clockwise. **/
  clockwise: PropTypes.bool,
  /** Data array of series, which can be a single data value, like:
   `[12, 34, 56, 10, 23]`
   Or, if need extra dimensions for components like visualMap to map to graphic attributes like color, it can also be in the form of array. For example:
   `[[12, 14], [34, 50], [56, 30], [10, 15], [23, 10]]`
   In this case, we can assign the second value in each array item to visualMap component.

   More likely, we need to assign name to each data item, in which case each item should be an object:
   `[{
   // name of date item
   name: 'data1',
   // value of date item is 8
   value: 10
   }, {
   name: 'data2',
   value: 20
   }]`
   Each data item can be further customized:
   `[{
   name: 'data1',
   value: 10
   }, {
   // name of data item
   name: 'data2',
   value : 56,
   // user-defined label format that only useful for this data item
   label: {},
   // user-defined special itemStyle that only useful for this data item
   itemStyle:{}
   }]
   **/
  data: PropTypes.array,
  /** The minimum data value which map to minAngle. **/
  min: PropTypes.number,
  /** The maximum data value which map to maxAngle. **/
  max: PropTypes.number,
  /** The number of split segments of gauge chart scale. **/
  splitNumber: PropTypes.number,
  /** The related configuration about the axis line of gauge chart. **/
  axisLine: PropTypes.object,
  /** Used to show current progress. **/
  progress: PropTypes.object,
  /** The style of split line. **/
  splitLine: PropTypes.object,
  axisTick: PropTypes.object,
  axisLabel: PropTypes.object,
  /** Gauge chart pointer **/
  pointer: PropTypes.object,
  /** The fixed point of a pointer in a dial. **/
  anchor: PropTypes.object,
  /** The style of gauge chart. **/
  itemStyle: PropTypes.object,
  /** Configurations of emphasis state. **/
  emphasis: PropTypes.object,
  /** The title of gauge chart. **/
  title: PropTypes.object,
  /** The detail about gauge chart which is used to show data. **/
  detail: PropTypes.object,
  /** Mark point in a chart. **/
  markPoint: PropTypes.object,
  /** Use a line in the chart to illustrate. **/
  markLine: PropTypes.object,
  /** Used to mark an area in chart. For example, mark a time interval. **/
  markArea: PropTypes.object,
  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. **/
  silent: PropTypes.bool,
  /** Whether to enable animation. **/
  animation: PropTypes.bool,
  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. **/
  animationThreshold: PropTypes.number,
  /** Duration of the first animation, which supports callback function for different data to have different animation effect **/
  animationDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. **/
  animationEasing: PropTypes.string,
  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. **/
  animationDelay: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Time for animation to complete, which supports callback function for different data to have different animation effect: **/
  animationDurationUpdate: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Easing method used for animation. **/
  animationEasingUpdate: PropTypes.string,
  /** Delay before updating animation, which supports callback function for different data to have different animation effects. **/
  animationDelayUpdate: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** tooltip settings in this series. **/
  tooltip: PropTypes.object
}

RingChart.defaultProps = {
  min: 0,
  max: 100,
  center: ['50%', '50%'],
  radius: '50%',
  startAngle: 90,
  endAngle: -270,
  axisLine: {
    lineStyle: {
      width: 20,
      color: [[1, '#4840bb3c']]
    }
  },
  splitLine: {
    show: false,
    distance: 0,
    length: 10
  },
  axisTick: {
    show: false
  },

  // hide gauge labels
  axisLabel: {
    show: false,
    distance: 50
  },
  title: {
    show: true
  },
  progress: {
    show: true
  },
  detail: {
    width: 50,
    height: 14,
    fontSize: 14,
    color: 'inherit',
    borderColor: 'inherit',
    borderRadius: 20,
    borderWidth: 1,
    formatter: '{value}'
  }
}

export default RingChart
